import {Component, computed, input} from "@angular/core";
import type {ValidationErrors} from "@angular/forms";
import {allEqualsUnordered} from "../../helpers/Equatable";
import type {Nullable, Optional} from "../../helpers/TypeHelpers";

export function combineValidationErrors(...validationErrors: readonly Readonly<Nullable<ValidationErrors>>[]): ValidationErrors {
  return validationErrors
    .filter(e => e !== null)
    .reduce((acc, curr) => ({...acc, ...curr}), {});
}

interface ControlError {
  readonly id: string;
  readonly message: string;
}

function isEqualControlError(lhs: Readonly<ControlError>, rhs: Readonly<ControlError>): boolean {
  return lhs.id === rhs.id && lhs.message === rhs.message;
}

interface KnownErrors {
  readonly required: unknown;
  readonly min: { readonly min: number };
  readonly max: { readonly max: number };
  readonly minlength: { readonly requiredLength: number };
  readonly maxlength: { readonly requiredLength: number };
  readonly pattern: { readonly requiredPattern: string };
  readonly email: unknown;
}

type ErrorEntry = {
  [K in keyof ValidationErrors]: {
    readonly key: K;
    readonly error: K extends keyof KnownErrors ? KnownErrors[K] : ValidationErrors[K];
  }
}[keyof ValidationErrors];

function getMessage(entry: ErrorEntry): Optional<string> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getError<K extends keyof KnownErrors>(entry: ErrorEntry, _: K): KnownErrors[K] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return entry.error as KnownErrors[K];
  }

  switch (entry.key) {
    case "required": {
      // We currently don't add a message. The field will be marked red anyway.
      return undefined;
    }
    case "min": {
      const error = getError(entry, entry.key);
      return $localize`:@@input-validation.errors.min:Must be at least ${error.min}:minimum:.`;
    }
    case "max": {
      const error = getError(entry, entry.key);
      return $localize`:@@input-validation.errors.max:Must be at most ${error.max}:maximum:.`;
    }
    case "minlength": {
      const error = getError(entry, entry.key);
      return $localize`:@@input-validation.errors.minlength:Must be at least ${error.requiredLength}:minimum_length: characters long.`;
    }
    case "maxlength": {
      const error = getError(entry, entry.key);
      return $localize`:@@input-validation.errors.maxlength:Must be at most ${error.requiredLength}:maximum_length: characters long.`;
    }
    case "pattern": {
      const error = getError(entry, entry.key);
      return $localize`:@@input-validation.errors.pattern:Must match '${error.requiredPattern}:pattern:'.`;
    }
    case "email": {
      return $localize`:@@input-validation.errors.email:Must be a valid email address.`;
    }
    default:
      return undefined;
  }
}

@Component({
  selector: "rf-control-validation-errors",
  templateUrl: "./control-validation-errors.html",
  host: {
    "[class.invalid-feedback]": "true",
  },
})
export class ControlValidationErrors {
  public readonly errors = input.required<Nullable<ValidationErrors>>();
  public readonly ignoredErrors = input<readonly string[]>([]);

  protected readonly controlErrors = computed<readonly ControlError[]>(() => {
    const errors = this.errors();
    if (errors === null) return [];
    const controlErrors: ControlError[] = [];
    for (const [key, error] of Object.entries(errors)) {
      if (this.ignoredErrors().includes(key)) continue;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const message = getMessage({key, error});
      if (message !== undefined) controlErrors.push({id: key, message});
    }
    return controlErrors;
  }, {equal: (a, b) => allEqualsUnordered(a, b, isEqualControlError)});
}
